/**
 * Central Configuration Registry
 *
 * Defines all configurable options for cloud tasks.
 * Each configuration item has:
 * - id: Unique identifier (used as key in taskConfig)
 * - category: Category for grouping (auto-derived for display)
 * - label: Display label
 * - emoji: Visual indicator
 * - tags: Search/filter tags
 * - description: Short description
 * - required: Whether this config is required
 * - order: Display order within category
 * - Component: Custom React component for editing
 * - defaultConfig: Default values
 * - getPreview: Function to generate preview text from config
 */

import DataCleaningConfig from "../components/ConfigComponents/DataCleaningConfig";
import AutomationConfig from "../components/ConfigComponents/AutomationConfig";
import GeneralConfig from "../components/ConfigComponents/GeneralConfig";
import PageUnblockerConfig from "../components/ConfigComponents/PageUnblockerConfig";
import CustomCommandsConfig from "../components/ConfigComponents/CustomCommandsConfig";
import ProxyConfig from "../components/ConfigComponents/ProxyConfig";
import UserAgentConfig from "../components/ConfigComponents/UserAgentConfig";
import CookiesStorageConfig from "../components/ConfigComponents/CookiesStorageConfig";
import GoogleSheetsExportConfig from "../components/ConfigComponents/GoogleSheetsExportConfig";
import WebhookExportConfig from "../components/ConfigComponents/WebhookExportConfig";
import { AUTOMATION_TYPES } from "./automationTypes";
import { getCountryEmoji, formatCityName } from "../utils/proxyHelpers";
import { DEVICE_PRESETS, formatBrowserInfo } from "../utils/devicePresets";

export const CONFIGURATIONS = [
  {
    id: "general",
    category: "general",
    label: "General",
    emoji: "ℹ️",
    tags: ["general", "name", "metadata", "info"],
    description: "Task name and general information",
    required: false,
    order: 1,
    showHeader: false,
    Component: GeneralConfig,
    defaultConfig: {
      name: "",
      websiteUrl: "",
      createdAt: null,
      updatedAt: null,
    },
    getPreview: (config) => {
      if (config?.name) {
        return config.name.length > 40 ? config.name.substring(0, 40) + "..." : config.name;
      }
      return "Edit task name and view details";
    },
  },
  {
    id: "automation",
    category: "core",
    label: "Your Automation",
    emoji: "🤖",
    tags: ["automation", "core", "settings", "json"],
    description: "Configure your automation settings",
    required: true,
    // Shopify automations are authored via the MCP agent (store detection / collection resolution),
    // not editable in this dashboard — exclude the generic automation editor for them.
    automationTypes: ["listExtractor", "pageExtractor", "emailExtractor", "socialLinkExtractor"],
    order: 1,
    showHeader: false,
    Component: AutomationConfig,
    defaultConfig: {},
    getPreview: (config, automationType) => {
      const name = AUTOMATION_TYPES[automationType]?.name || "Configure automation settings";
      if (automationType === "listExtractor" && config?.paginationMethod) {
        const paginationLabels = {
          scroll: "Auto-Scroll",
          navigate: "Page Navigation",
          click_button: "Load More",
        };
        const paginationLabel = paginationLabels[config.paginationMethod];
        if (paginationLabel) {
          return `${name} | ${paginationLabel}`;
        }
      }
      if (automationType === "pageExtractor" || automationType === "emailExtractor" || automationType === "socialLinkExtractor") {
        const urlCount = config?.urls?.length || 0;
        if (urlCount > 0) {
          return `${name} | ${urlCount} URL${urlCount !== 1 ? "s" : ""}`;
        }
      }
      return name;
    },
  },
  {
    id: "pageUnblocker",
    category: "pageUnblocker",
    label: "Page Unblocker",
    showHeader: false,
    emoji: "🔓",
    tags: ["popups", "cookies", "gdpr", "captcha", "turnstile", "datadome", "block", "newsletter"],
    description: "Handle captchas, popups, and page blocks automatically",
    required: false,
    // Not relevant for contact extraction (email / social links)
    automationTypes: ["listExtractor", "pageExtractor"],
    order: 1,
    Component: PageUnblockerConfig,
    defaultConfig: {
      captcha: "error",
      overlay: "handle",
      hardBlock: "error",
      overrides: {
        consent_banner: "handle",
        newsletter_popup: "warn",
        country_chooser: "warn",
        age_verification: "warn",
        other_popup: "warn",
        // Captcha overrides - only Cloudflare Turnstile can be solved
        cloudflare_turnstile: "handle",
        datadome: "error",
        recaptcha_v2: "error",
        recaptcha_v3: "error",
        hcaptcha: "error",
        custom_captcha: "error",
      },
      handlerInputs: {
        country_chooser: { country: "" },
      },
      maxBlockersToProcess: 2,
      minDelayBetweenAttempts: 2000,
      detectionMode: "text",
    },
    getPreview: (config) => {
      return "Dismiss popups, captchas, and access blocks"
    },
  },
  {
    id: "customCommands",
    category: "beforeRun",
    label: "Custom Commands",
    emoji: "🎯",
    tags: ["commands", "custom", "actions", "ai", "before", "prep", "setup"],
    description: "Execute AI-powered actions on the page before extraction",
    required: false,
    order: 2,
    disabled: true,
    Component: CustomCommandsConfig,
    defaultConfig: {
      commands: [],
      enabled: false,
      onFailure: "continue",
      retryOptions: {
        maxAttempts: 2,
        delayMs: 2000,
      },
    },
    getPreview: (config) => {
      if (!config || !config.enabled || !config.commands?.length) {
        return "No custom commands configured";
      }
      const enabledCount = config.commands.filter((cmd) => cmd.enabled).length;
      if (enabledCount === 0) return "All commands disabled";
      if (enabledCount === 1) {
        const cmd = config.commands.find((c) => c.enabled);
        return cmd.label || "1 custom command";
      }
      return `${enabledCount} custom commands`;
    },
  },
  {
    id: "cookiesStorage",
    category: "cookiesStorage",
    label: "Cookies & Storage",
    emoji: "\ud83c\udf6a",
    tags: ["cookies", "localStorage", "storage", "auth", "state", "login"],
    description: "Pre-load cookies and localStorage into cloud browser",
    required: false,
    automationTypes: ["listExtractor", "pageExtractor"],
    order: 1,
    showHeader: false,
    Component: CookiesStorageConfig,
    defaultConfig: {
      syncCookies: true,
      syncLocalStorage: true,
      cookies: [],
      localStorage: [],
    },
    isConfigured: (config) => {
      const cookieCount = config?.syncCookies !== false ? config?.cookies?.length || 0 : 0;
      const localCount = config?.syncLocalStorage !== false ? config?.localStorage?.length || 0 : 0;
      return cookieCount + localCount > 0;
    },
    getPreview: (config) => {
      const cookieCount = config?.cookies?.length || 0;
      const localCount = config?.localStorage?.length || 0;
      const total = cookieCount + localCount;
      if (total === 0) return "Clone your session to cloud browser";
      const parts = [];
      if (cookieCount > 0 && config?.syncCookies !== false) parts.push(`${cookieCount} cookie${cookieCount !== 1 ? "s" : ""}`);
      if (localCount > 0 && config?.syncLocalStorage !== false) parts.push(`${localCount} localStorage`);
      if (parts.length === 0) return "Session captured (sync disabled)";
      return parts.join(", ");
    },
  },
  {
    id: "proxyConfiguration",
    category: "browserLocation",
    label: "Proxy Location",
    emoji: "🌍",
    tags: ["proxy", "location", "country", "city", "isp", "residential", "geo"],
    description: "Set browser location using ISP or residential proxies",
    required: false,
    // The Shopify extractor is browser-free and always fetches through the ISP proxy internally,
    // so a per-task proxy location doesn't apply — hide it for Shopify automations.
    automationTypes: ["listExtractor", "pageExtractor", "emailExtractor", "socialLinkExtractor"],
    order: 1,
    Component: ProxyConfig,
    defaultConfig: {
      proxyType: "isp",
      country: null,
      city: null,
    },
    getPreview: (config) => {
      let preview;
      if (!config) {
        preview = "Random location (Residential)";
      } else {
        const typeLabel = config.proxyType === "isp" ? "ISP" : "Residential";
        if (!config.country) {
          preview = `Random location (${typeLabel})`;
        } else {
          const countryFlag = getCountryEmoji(config.country.code);
          const cityPart = config.city ? `, ${formatCityName(config.city)}` : "";
          preview = `${countryFlag} ${config.country.name}${cityPart} (${typeLabel})`;
        }
      }
      return `Proxy: ${preview}`;
    },
  },
  {
    id: "userAgentConfiguration",
    category: "userAgent",
    label: "User Agent & Viewport",
    emoji: "📱",
    tags: ["user-agent", "device", "browser", "viewport", "mobile"],
    description: "Set browser user agent and device viewport",
    required: false,
    // Browser-only setting; the Shopify extractor runs no browser, so hide it for Shopify automations.
    automationTypes: ["listExtractor", "pageExtractor", "emailExtractor", "socialLinkExtractor"],
    order: 1,
    Component: UserAgentConfig,
    defaultConfig: null, // Will be prefilled with detected browser info on mount
    getPreview: (config) => {
      // Show actual detected values for current browser
      if (!config?.preset || config.preset === "current") {
        if (config?.detectedBrowser) {
          return formatBrowserInfo(config.detectedBrowser);
        }
        return "Using saved browser settings";
      }
      const preset = DEVICE_PRESETS[config.preset];
      return `${preset.label} (${preset.viewport.width}x${preset.viewport.height})`;
    },
  },
  {
    id: "dataCleaning",
    category: "dataCleaning",
    label: "Data Cleaning",
    showHeader: false,
    emoji: "\ud83e\uddf9",
    tags: ["data", "cleaning", "filter", "duplicates", "empty", "optimize"],
    description: "Clean and optimize extracted data automatically",
    required: false,
    // Not relevant for contact extraction (email / social links)
    automationTypes: ["listExtractor", "pageExtractor"],
    order: 1,
    Component: DataCleaningConfig,
    defaultConfig: {
      removeEmptyRows: true,
      removeEmptyColumns: true,
      removeDuplicateColumns: true,
      removeSparseColumns: true,
      removeRepeatingColumns: true,
      sortColumnsByDensity: true,
      sparseThreshold: 0.7,
      repeatingThreshold: 0.999,
    },
    getPreview: (config) => {
      if (!config) return "Clean empty rows/columns, remove duplicates";
      const items = [];
      if (config.removeEmptyRows) items.push("empty rows");
      if (config.removeEmptyColumns) items.push("empty cols");
      if (config.removeSparseColumns) items.push("sparse cols");
      if (config.removeRepeatingColumns) items.push("repeating");
      if (config.removeDuplicateColumns) items.push("duplicates");
      return items.length > 0 ? `Remove: ${items.join(", ")}` : "No cleaning enabled";
    },
  },
  {
    id: "googleSheetsExport",
    category: "integrations",
    label: "Google Sheets",
    showHeader: false,
    emoji: "\ud83d\udcca",
    tags: ["google", "sheets", "export", "spreadsheet", "integration"],
    description: "Auto-export data to Google Sheets after each run",
    required: false,
    // Post-run auto-export is triggered by the workers' runPostProcessing step, which the browser-free
    // Shopify worker (extract_shopify) does not run — so hide it for Shopify rather than offer an
    // export that would silently never fire. (One-off export is still available from the data table.)
    automationTypes: ["listExtractor", "pageExtractor", "emailExtractor", "socialLinkExtractor"],
    order: 1,
    Component: GoogleSheetsExportConfig,
    defaultConfig: {
      enabled: false,
      mode: "create",
      spreadsheetId: null,
      spreadsheetName: null,
      spreadsheetUrl: null,
      sheetName: "Sheet1",
      includeHeader: true,
    },
    isConfigured: (config) => config?.enabled === true,
    getPreview: (config) => {
      if (!config?.enabled) return null;
      const modeLabels = {
        create: "Create new spreadsheet",
        replace: "Replace existing data",
        append: "Append new rows",
      };
      const modeLabel = modeLabels[config.mode] || "Create new spreadsheet";
      if (config.spreadsheetName) {
        return `${modeLabel} · ${config.spreadsheetName}`;
      }
      return modeLabel;
    },
  },
  {
    id: "webhookExport",
    category: "integrations",
    label: "Webhook",
    showHeader: false,
    emoji: "\ud83d\udd17",
    tags: ["webhook", "api", "export", "integration", "http", "post"],
    description: "Send data to your server via webhook after each run",
    required: false,
    // See googleSheetsExport above: the Shopify worker skips runPostProcessing, so an after-run
    // webhook would never be delivered — hide it for Shopify instead of promising it.
    automationTypes: ["listExtractor", "pageExtractor", "emailExtractor", "socialLinkExtractor"],
    order: 2,
    Component: WebhookExportConfig,
    defaultConfig: {
      enabled: false,
    },
    isConfigured: (config) => config?.enabled === true,
    getPreview: (config) => {
      if (!config?.enabled) return null;
      return "Webhook enabled";
    },
  },
];

/**
 * Get configuration by ID
 */
export const getConfigurationById = (id) => CONFIGURATIONS.find((config) => config.id === id);

/**
 * Get all configurations for a specific category, sorted by order
 */
export const getConfigurationsByCategory = (category, automationType = null) =>
  CONFIGURATIONS
    .filter((config) => !config.disabled)
    .filter((config) => config.category === category)
    .filter((config) => !automationType || !config.automationTypes || config.automationTypes.includes(automationType))
    .sort((a, b) => a.order - b.order);

/**
 * Get all unique category IDs from configurations
 */
export const getUniqueCategories = () => [...new Set(CONFIGURATIONS.filter((config) => !config.disabled).map((config) => config.category))];
